import { Card, Grid, Slider, Typography } from "@mui/material";
import { Member } from "../../../../service/model/member.model";
import DateRangeInput from "../../../../components/form-input-field-wrappers/date-range-input";
import { useEffect } from "react";
import dayjs from "dayjs";
import StyledChip from "../../../../components/styled-chip";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { selectActivePlan, updateActivePlan } from "../nutrition-plan.slice";
import { NutritionPlan } from "../../../../service/model/nutrition.model";
import FormField from "../../../../components/form-controls/form-field";

export interface PlanHeaderProps {
    member: Member;
}

const PlanHeader: React.FC<PlanHeaderProps> = ({ member }) => {
    const dispatch = useAppDispatch();
    const activePlan = useAppSelector(selectActivePlan);

    useEffect(() => {
        dispatch(updateActivePlan({
            ...activePlan,
            startDate: activePlan?.startDate ? dayjs(activePlan.startDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
            endDate: activePlan?.endDate ? dayjs(activePlan.endDate).format('YYYY-MM-DD') : dayjs().add(30, 'day').format('YYYY-MM-DD'),
            cycleLength: activePlan?.cycleLength ?? activePlan?.planSnapshot?.days?.length ?? 1,
        } as NutritionPlan));
    }, []);


    const handleDateChange = (start: Date | null, end: Date | null) => {
        dispatch(updateActivePlan({
            ...activePlan,
            startDate: start ? dayjs(start).format('YYYY-MM-DD') : activePlan?.startDate,
            endDate: end ? dayjs(end).format('YYYY-MM-DD') : activePlan?.endDate,
        } as NutritionPlan));
    };


    return (
        <Card sx={{ p: 2, justifyContent: 'center', alignItems: 'center', gap: 2, justifySelf: 'center', display: 'flex', flexDirection: 'column' }}>
            <StyledChip label={<Typography variant="body2">{`${member.firstName} ${member.lastName}`}</Typography>} size="medium" color="primary" />
            <Grid container direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ display: 'flex', width: '100%' }}>
                <DateRangeInput
                    initialStartDate={activePlan?.startDate ? dayjs(activePlan.startDate).toDate() : dayjs().toDate()}
                    initialEndDate={activePlan?.endDate ? dayjs(activePlan.endDate).toDate() : dayjs().add(30, 'day').toDate()}
                    onDateChange={handleDateChange}
                />
                <FormField>
                    <Typography id="input-slider" gutterBottom>
                        Cycle Length (Days)
                    </Typography>                    
                    <Typography variant="caption" id="input-slider" gutterBottom>
                        Meal plan will repeat after this many days.
                    </Typography>
                    <Slider
                        aria-label="Cycle Length"
                        defaultValue={1}
                        getAriaValueText={(value) => `${value} Day${value > 1 ? 's' : ''}`}
                        valueLabelDisplay="auto"
                        shiftStep={1}
                        step={1}
                        marks={[
                            { value: 1, label: '1' },
                            { value: 2, label: '2' },
                            { value: 3, label: '3' },
                            { value: 4, label: '4' },
                            { value: 5, label: '5' },
                            { value: 6, label: '6' },
                            { value: 7, label: '7' },
                        ]}
                        min={1}
                        max={7}
                        value={activePlan?.cycleLength ?? 1}
                        onChange={(e, value) => {
                            dispatch(updateActivePlan({
                                ...activePlan,
                                cycleLength: value,
                                planSnapshot: {
                                    ...activePlan?.planSnapshot,
                                    days: Array.from({ length: value as number }, (_, index) => ({
                                        day: index + 1,
                                        meals: activePlan?.planSnapshot?.days[index]?.meals || [],
                                        totalDayNutrition: activePlan?.planSnapshot?.days[index]?.totalDayNutrition || {
                                            calories: 0,
                                            protein: 0,
                                            carbohydrates: 0,
                                            fat: 0,
                                            fiber: 0,
                                            sugar: 0,
                                        }
                                    }))
                                }
                            } as NutritionPlan));
                        }}
                    />
                </FormField>
            </Grid>
        </Card>
    );
};

export default PlanHeader;
